/**
 * 問い合わせ `/support`（A97 / A147 / R71）。
 *
 * 3 つのタブで「人が返事をしなければならないもの」を 1 か所に集める:
 *   - 問い合わせ  … `support_tickets`。契約者面・公開窓口（abuse@ / security@ /
 *                   legal@）・運営の代理起票が全部ここに落ちる。
 *   - 権利行使要求 … `data_subject_requests`。個人情報保護法の開示・訂正・
 *                   利用停止。**期限は受付時に確定していて延ばせない。**
 *   - 運営通知    … `notifications(audience='staff')`。ワーカーが上げる
 *                   critical（ノード無応答・バックアップ異常・WORM 未アンカー・
 *                   到達性の悪化）は全部この表に入るのに、**読む口がどこにも
 *                   無かった**。critical はメールにも昇格するが、warning / info は
 *                   事実上どこにも出ていない。
 *
 * **期限の判定はサーバーの返り値をそのまま使う**（`firstResponseOverdue` /
 * `overdue`）。一次応答の期限は営業時間つきで計算されるので、画面側で
 * 計算し直すと必ずずれ、しかも「画面に出ている期限」のほうが信用される。
 *
 * **一次応答に数えるのは契約者に見える返信だけ**（サーバーの実装がそうなっている）。
 * 内部メモで期限を消せる形にすると SLA が自己申告になるため、返信フォームでも
 * 「社内メモ」を選んだときにその旨を明示する。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Check,
  Inbox,
  Lock,
  Plus,
  ScrollText,
  Send,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  Input,
  KeyValue,
  PageHeader,
  Section,
  StatCard,
  StatusBadge,
  Textarea,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import {
  DSR_KIND_LABEL,
  DSR_STATUS_LABEL,
  DSR_STATUS_TONE,
  NOTIFICATION_SEVERITY_LABEL,
  NOTIFICATION_SEVERITY_TONE,
  SUPPORT_KIND_LABEL,
  SUPPORT_PRIORITY_LABEL,
  SUPPORT_PRIORITY_TONE,
  SUPPORT_SOURCE_LABEL,
  SUPPORT_STATUS_LABEL,
  SUPPORT_STATUS_TONE,
  labelFrom,
  optionsFromLabels,
  toneFrom,
} from '../lib/ops-labels.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar } from '../components/filter-bar.js';
import { FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { PageTabs } from '../components/page-tabs.js';
import { DetailSkeleton, ListEmpty, PermissionDenied, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  CursorPage,
  PrivacyRequestEntry,
  StaffNotification,
  StaffUser,
  SupportSummary,
  SupportTicketDetail,
  SupportTicketEntry,
} from '../types/api.js';

const routeApi = getRouteApi(RID.support);

export function SupportPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canReadTickets = usePermission(OPS_PERMISSION.supportTicketRead);
  const canReadSecurity = usePermission(OPS_PERMISSION.securityRead);

  const summary = useQuery({
    queryKey: qk.supportSummary(),
    queryFn: () => api.get<SupportSummary>('/support/summary'),
    enabled: canReadTickets,
    refetchInterval: 60_000,
  });

  return (
    <>
      <PageHeader
        title="問い合わせ"
        description="問い合わせ・権利行使要求・運営向け通知。人が返事をしなければ止まるものだけを集めています。"
        actions={<RefetchingDot active={summary.isFetching && !summary.isLoading} />}
      />

      {summary.data ? (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label="未クローズ" value={summary.data.tickets.open} unit="件" icon={Inbox} />
          <StatCard
            label="未割当"
            value={summary.data.tickets.unassigned}
            unit="件"
            tone={summary.data.tickets.unassigned > 0 ? 'warning' : 'default'}
            hint="担当者が決まっていないもの"
          />
          <StatCard
            label="一次応答の期限超過"
            value={summary.data.tickets.overdue}
            unit="件"
            tone={summary.data.tickets.overdue > 0 ? 'destructive' : 'success'}
            hint="SLA の一次応答を過ぎたまま未応答"
          />
          <StatCard
            label="権利行使要求"
            value={summary.data.privacyRequests.open}
            unit="件"
            tone={summary.data.privacyRequests.overdue > 0 ? 'destructive' : 'default'}
            hint={
              summary.data.privacyRequests.overdue > 0
                ? `うち ${summary.data.privacyRequests.overdue} 件が期限超過`
                : '対応中のもの'
            }
          />
        </div>
      ) : null}

      <PageTabs
        tabs={[
          { value: 'tickets' as const, label: '問い合わせ', icon: Inbox, visible: canReadTickets },
          { value: 'privacy' as const, label: '権利行使要求', icon: ScrollText, visible: canReadSecurity },
          { value: 'notifications' as const, label: '運営通知', icon: Bell, visible: canReadSecurity },
        ]}
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
      />

      {search.tab === 'tickets' ? (
        canReadTickets ? (
          <TicketsTab />
        ) : (
          <PermissionDenied />
        )
      ) : null}
      {search.tab === 'privacy' ? canReadSecurity ? <PrivacyTab /> : <PermissionDenied /> : null}
      {search.tab === 'notifications' ? (
        canReadSecurity ? (
          <NotificationsTab />
        ) : (
          <PermissionDenied />
        )
      ) : null}
    </>
  );
}

/* ========================================================== 問い合わせ */

function TicketsTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.supportTicketWrite);
  const [createOpen, setCreateOpen] = React.useState(false);

  const list = useCursorList<SupportTicketEntry>({
    path: '/support/tickets',
    keyFor: (params) => qk.supportTickets(params),
    filters: {
      q: search.q,
      status: search.status,
      kind: search.kind,
      organizationId: search.organizationId,
      /*
       * **真のときだけ送る。** サーバー側は `z.coerce.boolean()` なので
       * `unassigned=false` は `Boolean('false') === true` で真として解釈される。
       * 「チェックを外したのに絞り込まれたまま」になるため、偽は送らない。
       */
      open: search.openOnly ? true : undefined,
      unassigned: search.unassigned ? true : undefined,
      overdue: search.overdue ? true : undefined,
    },
    refetchInterval: 60_000,
  });

  const select = (id: string): void => {
    // 一覧の下に詳細を出すだけ（同じ画面）。main を先頭へ戻すと選んだ行と詳細が画面外へ消える。
    void navigate({ search: (prev) => ({ ...prev, ticketId: id }), resetScroll: false });
  };

  const columns: Array<DataTableColumn<SupportTicketEntry>> = [
    {
      id: 'reference',
      header: '番号',
      width: 'w-32',
      cell: (t) => <code className="font-mono text-xs">{t.reference}</code>,
    },
    {
      id: 'subject',
      header: '件名',
      cell: (t) => (
        <div className="min-w-0 space-y-0.5">
          <span className="block truncate font-medium">{t.subject}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {labelFrom(SUPPORT_KIND_LABEL, t.kind)} ・ {t.requesterEmail}
          </span>
        </div>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      hideBelow: 'lg',
      cell: (t) =>
        t.organizationId ? (
          <Link
            to="/organizations/$orgId"
            params={{ orgId: t.organizationId }}
            search={{ tab: 'overview' }}
            className="hover:text-primary"
          >
            {t.organizationName}
          </Link>
        ) : (
          // 外部からの通報は組織に紐付かない。空欄にすると「紐付け忘れ」に見える。
          <span className="text-xs text-muted-foreground">{t.organizationName}</span>
        ),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-36',
      cell: (t) => (
        <StatusBadge
          status={t.status}
          size="sm"
          dot
          tone={toneFrom(SUPPORT_STATUS_TONE, t.status)}
          label={labelFrom(SUPPORT_STATUS_LABEL, t.status)}
        />
      ),
    },
    {
      id: 'priority',
      header: '優先度',
      width: 'w-24',
      hideBelow: 'md',
      cell: (t) => (
        <StatusBadge
          status={t.priority}
          size="sm"
          tone={toneFrom(SUPPORT_PRIORITY_TONE, t.priority)}
          label={labelFrom(SUPPORT_PRIORITY_LABEL, t.priority)}
        />
      ),
    },
    {
      id: 'due',
      header: '一次応答',
      hideBelow: 'md',
      // サーバーが判定した超過フラグを使う（期限は営業時間つきで計算されている）。
      cell: (t) =>
        t.firstRespondedAt ? (
          <span className="text-xs text-muted-foreground">応答済み</span>
        ) : t.firstResponseOverdue ? (
          <Badge variant="destructive" size="sm">
            期限超過
          </Badge>
        ) : t.firstResponseDueAt ? (
          <span className="text-xs text-warning">{formatRelative(t.firstResponseDueAt)}</span>
        ) : (
          '—'
        ),
    },
    {
      id: 'lastMessageAt',
      header: '最終更新',
      hideBelow: 'lg',
      cell: (t) => formatRelative(t.lastMessageAt),
    },
  ];

  return (
    <>
      <FilterBar
        q={search.q}
        onQChange={(v) => void navigate({ search: (prev) => ({ ...prev, q: v }) })}
        placeholder="件名・受付番号・依頼者のメールで絞り込む"
        active={hasActiveFilter(search, ['tab', 'ticketId', 'openOnly', 'dsrStatus', 'unreadOnly', 'severity'])}
        onClear={() =>
          void navigate({
            search: (prev) => ({
              ...prev,
              q: undefined,
              status: undefined,
              kind: undefined,
              organizationId: undefined,
              unassigned: undefined,
              overdue: undefined,
            }),
          })
        }
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            options: optionsFromLabels(SUPPORT_STATUS_LABEL),
            onChange: (v) =>
              void navigate({ search: (prev) => ({ ...prev, status: v as typeof search.status }) }),
          },
          {
            key: 'kind',
            label: '種別',
            value: search.kind,
            options: optionsFromLabels(SUPPORT_KIND_LABEL),
            onChange: (v) => void navigate({ search: (prev) => ({ ...prev, kind: v as typeof search.kind }) }),
          },
        ]}
      >
        <CheckboxFilter
          label="未クローズのみ"
          checked={search.openOnly}
          onChange={(v) => void navigate({ search: (prev) => ({ ...prev, openOnly: v }) })}
        />
        <CheckboxFilter
          label="未割当のみ"
          checked={search.unassigned ?? false}
          onChange={(v) => void navigate({ search: (prev) => ({ ...prev, unassigned: v || undefined }) })}
        />
        <CheckboxFilter
          label="期限超過のみ"
          checked={search.overdue ?? false}
          onChange={(v) => void navigate({ search: (prev) => ({ ...prev, overdue: v || undefined }) })}
        />
        <div className="ml-auto flex items-center gap-2">
          <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
          {canWrite ? (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden />
              代理で起票
            </Button>
          ) : null}
        </div>
      </FilterBar>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(t) => t.id}
        loading={list.query.isLoading}
        onRowClick={(t) => select(t.id)}
        selectedKey={search.ticketId ?? null}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search, ['tab', 'ticketId', 'dsrStatus', 'unreadOnly', 'severity'])}
            entityLabel="問い合わせ"
            onClearFilter={() =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  q: undefined,
                  status: undefined,
                  kind: undefined,
                  organizationId: undefined,
                  unassigned: undefined,
                  overdue: undefined,
                  openOnly: false,
                }),
              })
            }
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      {search.ticketId ? <TicketPanel ticketId={search.ticketId} canWrite={canWrite} /> : null}

      <CreateTicketDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={select} />
    </>
  );
}

/** フィルタ行に置くチェックボックス（`FilterBar` の children に入れる）。 */
function CheckboxFilter({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}): React.ReactElement {
  return (
    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border-border"
      />
      {label}
    </label>
  );
}

/* -------------------------------------------------------------- 詳細 */

function TicketPanel({ ticketId, canWrite }: { ticketId: string; canWrite: boolean }): React.ReactElement {
  const query = useQuery({
    queryKey: qk.supportTicket(ticketId),
    queryFn: () => api.get<SupportTicketDetail>(`/support/tickets/${ticketId}`),
  });

  if (query.isLoading) {
    return (
      <Section title="問い合わせの詳細" className="mt-6">
        <DetailSkeleton />
      </Section>
    );
  }
  if (query.isError) {
    return <QueryError error={query.error} onRetry={() => void query.refetch()} className="mt-6" />;
  }
  const ticket = query.data;
  if (!ticket) return <></>;

  return (
    <div className="mt-6 space-y-5">
      <Section title={`${ticket.reference} ${ticket.subject}`}>
        <KeyValue
          columns={3}
          items={[
            { key: '種別', value: labelFrom(SUPPORT_KIND_LABEL, ticket.kind) },
            { key: '受付経路', value: labelFrom(SUPPORT_SOURCE_LABEL, ticket.source) },
            { key: '状態', value: labelFrom(SUPPORT_STATUS_LABEL, ticket.status) },
            { key: '優先度', value: labelFrom(SUPPORT_PRIORITY_LABEL, ticket.priority) },
            { key: '依頼者', value: `${ticket.requesterName || '—'}（${ticket.requesterEmail}）` },
            { key: '担当者', value: ticket.assignedStaffUserId ?? '未割当' },
            { key: '受付', value: formatDateTime(ticket.createdAt) },
            { key: '一次応答の期限', value: formatDateTime(ticket.firstResponseDueAt) },
            { key: '一次応答', value: formatDateTime(ticket.firstRespondedAt) },
          ]}
        />
      </Section>

      <Section
        title="やり取り"
        description="社内メモ（グレー）は契約者には表示されません。一次応答に数えるのは契約者に見える返信だけです。"
      >
        <div className="space-y-3">
          {ticket.messages.map((m) => (
            <div
              key={m.id}
              className={
                m.visibility === 'internal'
                  ? 'rounded-lg border border-dashed border-border bg-panel px-3 py-2.5'
                  : 'rounded-lg border border-border bg-card px-3 py-2.5'
              }
            >
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{m.authorLabel}</span>
                <Badge variant="muted" size="sm">
                  {m.authorType === 'staff' ? '運営' : m.authorType === 'org_user' ? '契約者' : m.authorType}
                </Badge>
                {m.visibility === 'internal' ? (
                  <Badge variant="warning" size="sm">
                    <Lock aria-hidden />
                    社内メモ
                  </Badge>
                ) : null}
                <span className="ml-auto">{formatDateTime(m.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm">{m.bodyMd}</p>
            </div>
          ))}
          {ticket.messages.length === 0 ? (
            <p className="text-sm text-muted-foreground">本文がありません。</p>
          ) : null}
        </div>
      </Section>

      {canWrite ? <TicketActions ticket={ticket} /> : null}
    </div>
  );
}

/* -------------------------------------------------------------- 操作 */

function TicketActions({ ticket }: { ticket: SupportTicketDetail }): React.ReactElement {
  const qc = useQueryClient();
  const [body, setBody] = React.useState('');
  const [visibility, setVisibility] = React.useState<'public' | 'internal'>('public');
  const [assignOpen, setAssignOpen] = React.useState(false);

  const reply = useMutation({
    mutationFn: () => api.post(`/support/tickets/${ticket.id}/messages`, { bodyMd: body.trim(), visibility }),
    onSuccess: () => {
      setBody('');
      void qc.invalidateQueries({ queryKey: qk.supportTicket(ticket.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'support'] });
    },
  });

  const patch = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.patch(`/support/tickets/${ticket.id}`, values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.supportTicket(ticket.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'support'] });
    },
  });

  const closed = ticket.status === 'resolved' || ticket.status === 'closed';

  return (
    <>
      <Section title="返信する">
        {reply.isError ? <QueryError error={reply.error} className="mb-3" /> : null}
        <div className="space-y-3">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="お問い合わせありがとうございます。…"
          />
          <div className="flex flex-wrap items-center gap-3">
            <NativeSelect
              value={visibility}
              onChange={(v) => setVisibility(v as 'public' | 'internal')}
              className="h-9 w-auto"
              options={[
                { value: 'public', label: '契約者へ返信（メール通知あり）' },
                { value: 'internal', label: '社内メモ（契約者には出ない）' },
              ]}
            />
            <Button
              size="sm"
              onClick={() => reply.mutate()}
              loading={reply.isPending}
              disabled={body.trim().length === 0}
            >
              <Send aria-hidden />
              {visibility === 'public' ? '返信を送る' : 'メモを残す'}
            </Button>
            {visibility === 'internal' ? (
              <span className="text-xs text-muted-foreground">
                社内メモは一次応答に数えません（SLA の期限は止まりません）。
              </span>
            ) : null}
          </div>
        </div>
      </Section>

      <Section title="この問い合わせの扱い">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-48 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">状態</p>
            <NativeSelect
              value={ticket.status}
              onChange={(v) => patch.mutate({ status: v })}
              disabled={patch.isPending}
              options={optionsFromLabels(SUPPORT_STATUS_LABEL)}
            />
          </div>
          <div className="w-40 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">優先度</p>
            <NativeSelect
              value={ticket.priority}
              onChange={(v) => patch.mutate({ priority: v })}
              disabled={patch.isPending}
              options={optionsFromLabels(SUPPORT_PRIORITY_LABEL)}
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)}>
            担当者を割り当てる
          </Button>
        </div>
        {patch.isError ? <QueryError error={patch.error} className="mt-3" /> : null}
        {closed ? (
          <Alert className="mt-3">
            <AlertDescription>
              解決・クローズ済みです。状態を戻すと解決時刻が消えます（再オープンの扱い）。
            </AlertDescription>
          </Alert>
        ) : null}
      </Section>

      <AssignDialog ticket={ticket} open={assignOpen} onOpenChange={setAssignOpen} />
    </>
  );
}

function AssignDialog({
  ticket,
  open,
  onOpenChange,
}: {
  ticket: SupportTicketDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [staffId, setStaffId] = React.useState(ticket.assignedStaffUserId ?? '');

  const staff = useQuery({
    queryKey: qk.staff(),
    queryFn: () => api.get<CursorPage<StaffUser>>('/staff'),
    enabled: open,
  });

  React.useEffect(() => {
    if (open) setStaffId(ticket.assignedStaffUserId ?? '');
  }, [open, ticket.assignedStaffUserId]);

  const submit = useMutation({
    // `null` で未割当に戻せる（zod は `.nullable()`）。空文字は送らない。
    mutationFn: () => api.patch(`/support/tickets/${ticket.id}`, { assignedStaffUserId: staffId || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.supportTicket(ticket.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'support'] });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="担当者を割り当てる"
      description="未割当のまま滞留すると、誰も見ていない問い合わせになります。"
      submitLabel="割り当てる"
      pending={submit.isPending}
      error={submit.error}
      onSubmit={() => submit.mutate()}
    >
      <Field label="担当者">
        <NativeSelect
          value={staffId}
          onChange={setStaffId}
          options={[
            { value: '', label: '未割当に戻す' },
            ...(staff.data?.items ?? [])
              .filter((s) => s.status === 'active')
              .map((s) => ({ value: s.id, label: `${s.displayName}（${s.email}）` })),
          ]}
        />
      </Field>
    </FormDialog>
  );
}

/* -------------------------------------------------------------- 起票 */

function CreateTicketDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [organizationId, setOrganizationId] = React.useState('');
  const [kind, setKind] = React.useState('support');
  const [priority, setPriority] = React.useState('normal');
  const [subject, setSubject] = React.useState('');
  const [requesterEmail, setRequesterEmail] = React.useState('');
  const [requesterName, setRequesterName] = React.useState('');
  const [bodyMd, setBodyMd] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setOrganizationId('');
    setKind('support');
    setPriority('normal');
    setSubject('');
    setRequesterEmail('');
    setRequesterName('');
    setBodyMd('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ id: string; reference: string; firstResponseDueAt: string }>('/support/tickets', {
        // `.strict()` なので余分なキーは 400 になる。空の組織 ID は送らない。
        ...(organizationId.trim().length > 0 ? { organizationId: organizationId.trim() } : {}),
        kind,
        subject: subject.trim(),
        bodyMd: bodyMd.trim(),
        requesterEmail: requesterEmail.trim(),
        requesterName: requesterName.trim(),
        priority,
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['ops', 'support'] });
      onOpenChange(false);
      onCreated(created.id);
    },
  });

  const invalid =
    subject.trim().length === 0 || bodyMd.trim().length === 0 || !/^[^@\s]+@[^@\s]+$/.test(requesterEmail.trim());

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="問い合わせを代理で起票する"
      description="電話・メールで受けた問い合わせを台帳に載せます。受け付けた事実を残す場所を 1 つに保つための操作です。"
      submitLabel="起票する"
      pending={submit.isPending}
      error={submit.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
      wide
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="種別" required>
          <NativeSelect value={kind} onChange={setKind} options={optionsFromLabels(SUPPORT_KIND_LABEL)} />
        </Field>
        <Field label="優先度" required>
          <NativeSelect value={priority} onChange={setPriority} options={optionsFromLabels(SUPPORT_PRIORITY_LABEL)} />
        </Field>
      </div>

      <Field label="件名" required>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="依頼者のメールアドレス" required hint="返信の宛先になります。">
          <Input
            value={requesterEmail}
            onChange={(e) => setRequesterEmail(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field label="依頼者の氏名">
          <Input value={requesterName} onChange={(e) => setRequesterName(e.target.value)} maxLength={200} />
        </Field>
      </div>

      <Field label="契約組織 ID" hint="外部からの通報など、組織に紐付かないものは空のままにします。">
        <Input
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      <Field label="本文" required hint="受けた内容をそのまま残します。">
        <Textarea value={bodyMd} onChange={(e) => setBodyMd(e.target.value)} rows={5} />
      </Field>
    </FormDialog>
  );
}

/* ====================================================== 権利行使要求 */

function PrivacyTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.privacyRequestWrite);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<PrivacyRequestEntry | null>(null);

  const list = useCursorList<PrivacyRequestEntry>({
    path: '/privacy/requests',
    keyFor: (params) => qk.privacyRequests(params),
    filters: { status: search.dsrStatus },
  });

  const overdue = list.rows.filter((r) => r.overdue).length;

  const columns: Array<DataTableColumn<PrivacyRequestEntry>> = [
    {
      id: 'reference',
      header: '番号',
      width: 'w-32',
      cell: (r) => <code className="font-mono text-xs">{r.reference}</code>,
    },
    { id: 'kind', header: '請求の種類', cell: (r) => labelFrom(DSR_KIND_LABEL, r.kind) },
    {
      id: 'subject',
      header: '本人',
      hideBelow: 'md',
      cell: (r) => (
        <div className="min-w-0">
          <span className="block truncate text-sm">{r.subjectName || '—'}</span>
          <span className="block truncate text-xs text-muted-foreground">{r.subjectEmail}</span>
        </div>
      ),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-32',
      cell: (r) => (
        <StatusBadge
          status={r.status}
          size="sm"
          dot
          tone={toneFrom(DSR_STATUS_TONE, r.status)}
          label={labelFrom(DSR_STATUS_LABEL, r.status)}
        />
      ),
    },
    {
      id: 'verified',
      header: '本人確認',
      width: 'w-28',
      hideBelow: 'md',
      // 本人確認が取れていない請求は完了にできない（なりすましへの開示は漏えい）。
      cell: (r) =>
        r.verifiedAt ? (
          <Badge variant="success" size="sm">
            済
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            未
          </Badge>
        ),
    },
    {
      id: 'dueAt',
      header: '対応期限',
      cell: (r) =>
        r.overdue ? (
          <Badge variant="destructive" size="sm">
            {formatDateTime(r.dueAt)}
          </Badge>
        ) : (
          <span className="text-xs">{formatDateTime(r.dueAt)}</span>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-28',
      cell: (r) =>
        canWrite ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(r)}>
            対応を記録
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      {overdue > 0 ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>対応期限を過ぎた請求が {overdue} 件あります</AlertTitle>
          <AlertDescription>
            期限は受付時に確定していて、後から掲示（プライバシーポリシー）を変えても動きません。
          </AlertDescription>
        </Alert>
      ) : null}

      <FilterBar
        active={search.dsrStatus !== undefined}
        onClear={() => void navigate({ search: (prev) => ({ ...prev, dsrStatus: undefined }) })}
        selects={[
          {
            key: 'dsrStatus',
            label: '状態',
            value: search.dsrStatus,
            options: optionsFromLabels(DSR_STATUS_LABEL),
            onChange: (v) =>
              void navigate({ search: (prev) => ({ ...prev, dsrStatus: v as typeof search.dsrStatus }) }),
          },
        ]}
      >
        <div className="ml-auto flex items-center gap-2">
          <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
          {canWrite ? (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden />
              請求を受け付ける
            </Button>
          ) : null}
        </div>
      </FilterBar>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(r) => r.id}
        loading={list.query.isLoading}
        empty={<ListEmpty filtered={search.dsrStatus !== undefined} entityLabel="権利行使要求" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <CreatePrivacyRequestDialog open={createOpen} onOpenChange={setCreateOpen} />
      <UpdatePrivacyRequestDialog request={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function CreatePrivacyRequestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [kind, setKind] = React.useState('access');
  const [subjectEmail, setSubjectEmail] = React.useState('');
  const [subjectName, setSubjectName] = React.useState('');
  const [organizationId, setOrganizationId] = React.useState('');
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setKind('access');
    setSubjectEmail('');
    setSubjectName('');
    setOrganizationId('');
    setNotes('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/privacy/requests', {
        kind,
        subjectEmail: subjectEmail.trim(),
        subjectName: subjectName.trim(),
        ...(organizationId.trim().length > 0 ? { organizationId: organizationId.trim() } : {}),
        notes: notes.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'privacy'] });
      void qc.invalidateQueries({ queryKey: qk.supportSummary() });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="権利行使要求を受け付ける"
      description="受付日から 14 日の対応期限が自動で設定されます（プライバシーポリシーの掲示に合わせた値）。"
      submitLabel="受け付ける"
      pending={submit.isPending}
      error={submit.error}
      disabled={!/^[^@\s]+@[^@\s]+$/.test(subjectEmail.trim())}
      onSubmit={() => submit.mutate()}
      wide
    >
      <Field label="請求の種類" required>
        <NativeSelect value={kind} onChange={setKind} options={optionsFromLabels(DSR_KIND_LABEL)} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="本人のメールアドレス" required>
          <Input
            value={subjectEmail}
            onChange={(e) => setSubjectEmail(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field label="本人の氏名">
          <Input value={subjectName} onChange={(e) => setSubjectName(e.target.value)} maxLength={200} />
        </Field>
      </div>

      <Field label="契約組織 ID" hint="どの契約者のデータに関する請求か分かっていれば入れます。">
        <Input
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      <Field label="メモ" hint="受付の経緯。本人のメールアドレスは監査ログには残りません。">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </Field>
    </FormDialog>
  );
}

function UpdatePrivacyRequestDialog({
  request,
  onClose,
}: {
  request: PrivacyRequestEntry | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [status, setStatus] = React.useState('received');
  const [verified, setVerified] = React.useState(false);
  const [rejectedReason, setRejectedReason] = React.useState('');
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    if (!request) return;
    setStatus(request.status);
    setVerified(request.verifiedAt !== null);
    setRejectedReason(request.rejectedReason ?? '');
    setNotes(request.notes ?? '');
  }, [request]);

  const submit = useMutation({
    mutationFn: () =>
      api.patch(`/privacy/requests/${request?.id ?? ''}`, {
        status,
        verified,
        rejectedReason,
        notes,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'privacy'] });
      void qc.invalidateQueries({ queryKey: qk.supportSummary() });
      onClose();
    },
  });

  // サーバー側と同じ条件を先に見せる（押してから 400 で断られるより早い）。
  const blocked =
    (status === 'completed' && !verified) || (status === 'rejected' && rejectedReason.trim().length === 0);

  return (
    <FormDialog
      open={request !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${request?.reference ?? ''} の対応を記録する`}
      description="対応の状態・本人確認・拒否理由を残します。監査ログに記録されます。"
      submitLabel="記録する"
      pending={submit.isPending}
      error={submit.error}
      disabled={blocked}
      onSubmit={() => submit.mutate()}
      wide
    >
      <Field label="状態" required>
        <NativeSelect value={status} onChange={setStatus} options={optionsFromLabels(DSR_STATUS_LABEL)} />
      </Field>

      <label className="flex items-start gap-2 rounded-md border border-border bg-panel px-3 py-2.5 text-sm">
        <input
          type="checkbox"
          checked={verified}
          onChange={(e) => setVerified(e.target.checked)}
          className="mt-0.5 size-4 rounded border-border"
        />
        <span>
          本人確認が取れた
          <span className="mt-0.5 block text-xs text-muted-foreground">
            なりすましへの開示は、それ自体が漏えい（26 条の報告対象）になります。確認が取れるまで完了にできません。
          </span>
        </span>
      </label>

      {status === 'rejected' ? (
        <Field label="拒否の理由" required hint="法で拒否できる場合でも、本人への説明義務があります。">
          <Textarea value={rejectedReason} onChange={(e) => setRejectedReason(e.target.value)} rows={3} />
        </Field>
      ) : null}

      <Field label="メモ">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </Field>

      {blocked && status === 'completed' ? (
        <Alert variant="warning">
          <AlertDescription>本人確認が済んでいない請求は完了にできません。</AlertDescription>
        </Alert>
      ) : null}
    </FormDialog>
  );
}

/* ========================================================== 運営通知 */

function NotificationsTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.securityWrite);

  const list = useCursorList<StaffNotification>({
    path: '/notifications',
    keyFor: (params) => qk.notifications(params),
    filters: {
      // `z.coerce.boolean()` は 'false' も真にするので、真のときだけ送る。
      unread: search.unreadOnly ? true : undefined,
      severity: search.severity,
    },
    refetchInterval: 60_000,
  });

  const patch = useMutation({
    mutationFn: (input: { id: string; values: { read?: boolean; acknowledged?: boolean } }) =>
      api.patch(`/notifications/${input.id}`, input.values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'notifications'] });
    },
  });

  const columns: Array<DataTableColumn<StaffNotification>> = [
    {
      id: 'severity',
      header: '重大度',
      width: 'w-28',
      cell: (n) => (
        <StatusBadge
          status={n.severity}
          size="sm"
          dot
          pulse={n.severity === 'critical' && n.readAt === null}
          tone={toneFrom(NOTIFICATION_SEVERITY_TONE, n.severity)}
          label={labelFrom(NOTIFICATION_SEVERITY_LABEL, n.severity)}
        />
      ),
    },
    {
      id: 'title',
      header: '内容',
      cell: (n) => (
        <div className="min-w-0 space-y-0.5">
          <span className={n.readAt ? 'block truncate' : 'block truncate font-semibold'}>{n.title}</span>
          <span className="block truncate text-xs text-muted-foreground">{n.bodyMd}</span>
        </div>
      ),
    },
    {
      id: 'createdAt',
      header: '発生',
      width: 'w-40',
      hideBelow: 'md',
      cell: (n) => formatRelative(n.createdAt),
    },
    {
      id: 'ack',
      header: '対処',
      width: 'w-32',
      hideBelow: 'md',
      // 既読は「開いた」だけでも付く。対処の証跡には `acknowledged` を使う。
      cell: (n) =>
        n.acknowledgedAt ? (
          <Badge variant="success" size="sm">
            引き受け済み
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-48',
      cell: (n) =>
        canWrite ? (
          <div className="flex justify-end gap-1">
            {n.readAt === null ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => patch.mutate({ id: n.id, values: { read: true } })}
                disabled={patch.isPending}
              >
                既読にする
              </Button>
            ) : null}
            {n.acknowledgedAt === null ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => patch.mutate({ id: n.id, values: { read: true, acknowledged: true } })}
                disabled={patch.isPending}
              >
                <Check aria-hidden />
                対処を引き受ける
              </Button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <Alert className="mb-4">
        <AlertDescription>
          ワーカーが上げる警報（ノード無応答・バックアップ異常・監査 WORM の未アンカー・到達性の悪化）はここに入ります。
          <strong>「対処を引き受ける」は既読とは別</strong>で、人が見て対応を引き受けた証跡として残ります。
        </AlertDescription>
      </Alert>

      <FilterBar
        active={search.severity !== undefined || !search.unreadOnly}
        onClear={() =>
          void navigate({ search: (prev) => ({ ...prev, severity: undefined, unreadOnly: true }) })
        }
        selects={[
          {
            key: 'severity',
            label: '重大度',
            value: search.severity,
            options: optionsFromLabels(NOTIFICATION_SEVERITY_LABEL),
            onChange: (v) =>
              void navigate({ search: (prev) => ({ ...prev, severity: v as typeof search.severity }) }),
          },
        ]}
      >
        <CheckboxFilter
          label="未読のみ"
          checked={search.unreadOnly}
          onChange={(v) => void navigate({ search: (prev) => ({ ...prev, unreadOnly: v }) })}
        />
        <div className="ml-auto">
          <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
        </div>
      </FilterBar>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(n) => n.id}
        loading={list.query.isLoading}
        empty={<ListEmpty filtered={search.unreadOnly} entityLabel="通知" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />
    </>
  );
}
